import { z } from "zod";
import { isoDate } from "./dates";

export const billingPeriodKey = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Hónap formátum: ÉÉÉÉ-HH");

export const INVOICING_PROVIDERS = ["NONE", "MOCK", "SZAMLAZZ"] as const;
export type InvoicingProviderCode = (typeof INVOICING_PROVIDERS)[number];
export const INVOICING_PROVIDER_LABEL: Record<InvoicingProviderCode, string> = {
  NONE: "Nincs (csak piszkozat)",
  MOCK: "Teszt számlázó (nem valódi számla)",
  SZAMLAZZ: "számlázz.hu Számla Agent",
};

export const DRAFT_STATUS_LABEL = { DRAFT: "Piszkozat", APPROVED: "Jóváhagyva", ISSUED: "Kiállítva", DISCARDED: "Elvetve" } as const;
export const INVOICE_STATUS_LABEL = { ISSUED: "Nyitott", PARTIALLY_PAID: "Részben fizetve", PAID: "Kifizetve", STORNOED: "Stornózva" } as const;
export const LINE_SOURCE_LABEL = { CHARGE: "Díjtétel", METER: "Közüzem (almérő)", CORRECTION: "Korrekció", EXPENSE_RECHARGE: "Továbbszámlázás", MANUAL: "Kézi tétel" } as const;

/** Piszkozat-figyelmeztetések. A `blocking` kódok mellett nem hagyható jóvá a piszkozat. */
export const DRAFT_WARNINGS = {
  NO_BUYER_ADDRESS: { label: "Hiányzik a vevő számlázási címe", blocking: true },
  NO_BUYER_TAX_NUMBER: { label: "Hiányzik a vevő adószáma", blocking: false },
  NO_SELLER_ADDRESS: { label: "Hiányzik a bérbeadó címe", blocking: true },
  NO_BANK_ACCOUNT: { label: "A bérbeadónak nincs bankszámlája ebben a devizában", blocking: false },
  NO_FX_RATE: { label: "Hiányzó árfolyam – egy vagy több tétel nem számolható", blocking: true },
  NO_LINES: { label: "Nincs számlázandó tétel", blocking: true },
  NEGATIVE_TOTAL: { label: "A végösszeg negatív – jóváírás / storno lehet a helyes út", blocking: false },
  NO_PROVIDER: { label: "A bérbeadóhoz nincs számlázó beállítva", blocking: false },
  NO_UTILITY_PRICE: { label: "Hiányzik a közüzemi továbbszámlázási egységár", blocking: true },
  STALE_READING: { label: "Egy almérőnek nincs friss leolvasása – a fogyasztás egy része a következő számlára kerül", blocking: false },
} as const;
export type DraftWarning = keyof typeof DRAFT_WARNINGS;

export const AGING_BUCKETS = ["NOT_DUE", "D0_30", "D31_60", "D61_90", "D90_PLUS"] as const;
export type AgingBucket = (typeof AGING_BUCKETS)[number];
export const AGING_LABEL: Record<AgingBucket, string> = { NOT_DUE: "Nem esedékes", D0_30: "0–30 nap", D31_60: "31–60 nap", D61_90: "61–90 nap", D90_PLUS: "90+ nap" };
export function agingBucket(daysOverdue: number): AgingBucket {
  if (daysOverdue <= 0) return "NOT_DUE";
  if (daysOverdue <= 30) return "D0_30";
  if (daysOverdue <= 60) return "D31_60";
  if (daysOverdue <= 90) return "D61_90";
  return "D90_PLUS";
}

const money = z.coerce.number({ invalid_type_error: "Számot adj meg" }).min(-1e12).max(1e12);
const optStr = (max = 200) => z.string().trim().max(max).nullable().optional();

export const draftLineSchema = z.object({
  description: z.string().trim().min(1).max(300),
  quantity: z.coerce.number().min(-1e6).max(1e6).refine((v) => v !== 0, "Nem lehet 0"),
  unit: z.string().trim().min(1).max(20).default("db"),
  unitPrice: money,
  vatCode: z.string().trim().min(1).max(10).default("27"),
  glAccount: optStr(20),
  periodFrom: isoDate.nullable().optional(),
  periodTo: isoDate.nullable().optional(),
});
export type DraftLineInput = z.infer<typeof draftLineSchema>;

export const draftPatchSchema = z.object({
  issueDate: isoDate.optional(),
  fulfilmentDate: isoDate.optional(),
  dueDate: isoDate.optional(),
  note: optStr(1000),
  paymentMethod: z.string().trim().min(2).max(40).optional(),
});

export const newDraftSchema = z.object({
  leaseId: z.string().uuid(),
  legalEntityId: z.string().uuid().optional(),
  issueDate: isoDate.optional(),
});

export const paymentSchema = z.object({
  amount: money.refine((v) => v !== 0, "Nem lehet 0"),
  date: isoDate,
  method: z.string().trim().min(2).max(40).default("Átutalás"),
  reference: optStr(120),
  note: optStr(500),
});

export const periodLockSchema = z.object({ period: billingPeriodKey, note: optStr(500) });

export const exchangeRateSchema = z.object({
  currency: z.string().trim().toUpperCase().length(3),
  date: isoDate,
  rate: z.coerce.number().positive().max(1e6),
});

export const invoicingSettingsSchema = z.object({
  invoicingProvider: z.enum(INVOICING_PROVIDERS),
  /** Üres: a meglévő kulcs marad; null: törlés. */
  agentKey: z.string().trim().min(10).max(200).nullable().optional(),
});

/** Próbaszámlázás (PRP-2): a szervezet számlázz.hu TESZT fiókjának kulcsa; null: törlés. */
export const testInvoicingKeySchema = z.object({
  agentKey: z.string({ required_error: "Add meg a kulcsot" }).trim().min(10, "Legalább 10 karakter").max(200).nullable(),
});
export const trialMonthSchema = z.object({ month: billingPeriodKey });
export const trialRunSchema = z.object({ date: isoDate });

/** Egy próbaszámlázási tétel állapota: kiállítható, vagy miért nem kerül a próbafutásba. */
export const TRIAL_STATUS = {
  READY: "Kiállításra vár",
  BLOCKED: "Nem állítható ki",
  APPROVED: "Már jóváhagyva",
  ISSUED: "Már kiállítva",
} as const;
export type TrialStatus = keyof typeof TRIAL_STATUS;
